// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo } from 'react';
import { getAntdSelectOptions, isEmptyObject, useLocalMessage } from '@Helpers/';
import { useListAllFields } from '@Queries/describeQueries';
import { AllField } from '@Organisms/Dashboard/WidgetManage/AllField';
import Form, { FormInstance } from 'antd/lib/form';
import { THForm } from '@Molecules/THForm/THForm';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';

import './EntityField.scss';

interface EntityFieldProps {
    form: FormInstance;
    showDate?: boolean;
    showPeriodDate?: boolean;
    showCategory?: boolean;
    entitiesToExclude?: string[];
    showMaxFragmentsShown?: boolean;
}

const EntityField = ({
    form,
    showDate,
    showPeriodDate,
    showCategory,
    entitiesToExclude,
    showMaxFragmentsShown,
}: EntityFieldProps): ReactElement => {
    const i = useLocalMessage();
    const { data: describeData } = useListAllFields();
    const selectableEntities = useMemo(() => {
        const exclusionList = new Set(entitiesToExclude?.map((v) => v.toLowerCase()) || []);

        return Object.entries(describeData || {}).reduce((acc, [entity, { initialQuery }]) => {
            if (exclusionList.has(entity.toLowerCase()) || isEmptyObject(initialQuery)) {
                return acc;
            }

            return [...acc, entity];
        }, [] as string[]);
    }, [entitiesToExclude, describeData]);

    const entity = Form.useWatch(['entity'], form);

    return (
        <div className="entity-field">
            <div className="entity-field__section">
                <THForm.Item
                    className="entity-field__section__item"
                    name="entity"
                    label={i('app.dashboard.widget.create.form.entity.label')}
                    rules={[
                        {
                            required: true,
                            message: i('app.dashboard.widget.create.form.entity.required'),
                            validator: (_, value) => {
                                if (describeData && !Object.keys(describeData).includes(value)) {
                                    return Promise.reject();
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <THAutoComplete
                        placeholder={i('app.dashboard.widget.create.form.entity.placeholder')}
                        options={getAntdSelectOptions(selectableEntities)}
                    />
                </THForm.Item>

                {showCategory && (
                    <THForm.Item<any>
                        noStyle
                        shouldUpdate={(prevValues, nextValues) => prevValues['entity'] !== nextValues['entity']}
                    >
                        {() =>
                            describeData ? (
                                <AllField
                                    describeData={describeData}
                                    form={form}
                                    fieldType={[
                                        'user',
                                        'number',
                                        'string',
                                        'boolean',
                                        'enumeration',
                                        'float',
                                        'integer',
                                    ]}
                                    fieldName="category"
                                    formItemName="category"
                                    entity={form.getFieldValue('entity')}
                                    fieldsToExclude={['keyword']}
                                    indexTypeToExclude={['none']}
                                />
                            ) : null
                        }
                    </THForm.Item>
                )}
            </div>

            {(showDate || showPeriodDate) && (
                <div className="entity-field__section">
                    {showDate && (
                        <THForm.Item<any>
                            noStyle
                            shouldUpdate={(prevValues, nextValues): boolean =>
                                prevValues['entity'] !== nextValues['entity']
                            }
                        >
                            {() =>
                                describeData ? (
                                    <AllField
                                        describeData={describeData}
                                        fieldType={['date']}
                                        formItemName="dateField"
                                        fieldName="dateField"
                                        form={form}
                                        entity={form.getFieldValue('entity')}
                                        aggregationWarning={false}
                                    />
                                ) : null
                            }
                        </THForm.Item>
                    )}

                    {showPeriodDate && (
                        <THForm.Item<any>
                            noStyle
                            shouldUpdate={(prevValues, nextValues): boolean =>
                                prevValues['entity'] !== nextValues['entity']
                            }
                        >
                            {() =>
                                describeData ? (
                                    <AllField
                                        describeData={describeData}
                                        fieldType={['date']}
                                        formItemName="periodDateField"
                                        fieldName="periodDateField"
                                        form={form}
                                        entity={form.getFieldValue('entity')}
                                        aggregationWarning={false}
                                    />
                                ) : null
                            }
                        </THForm.Item>
                    )}

                    {showMaxFragmentsShown && (
                        <THForm.Item<any>
                            className="entity-field__section__item"
                            name="size"
                            label={i('app.dashboard.widget.create.form.fragments.label')}
                            help={i('app.dashboard.widget.create.form.fragments.helper')}
                            hidden={!entity}
                        >
                            <THInputNumber min={0} />
                        </THForm.Item>
                    )}
                </div>
            )}
        </div>
    );
};

export default EntityField;
